import * as React from 'react';
import { connect } from 'react-redux';
import { addToFavorites, removeFromFavorites } from '../../actions/favoritesPageActions';
import {
    clearFilterParams,
    doNewSearchViaUrl,
    getRatesBulk,
    loadMore,
    setFilterParam,
    setLoadingState,
    setSortOrder,
    toggleSearchFilter,
} from '../../actions/searchActions';
import { Discount } from '../../components/Discount/Discount';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator2';
import { ActiveFilters } from '../../components/SearchFilters/ActiveFilters';
import { FilterAndSortBar } from '../../components/SearchFilters/FilterAndSortBar';
import { SearchFilters } from '../../components/SearchFilters/SearchFilters';
import { IsomorphicHelpers } from '../../helpers/IsomorphicHelpers';
import { __ } from '../../helpers/TranslationService';
import { IMobileSearchResultCruise } from '../../interfaces/ISearchApi';
import { ISearchReducer } from '../../reducers/search';
import { FAQContact } from '../FAQ/components/FAQContact';
import { NoResult } from './components/NoResults';
import { SearchResultList } from './components/SearchResultList';

declare const window: Window;

export interface ISearchResultProps {
    cruise: IMobileSearchResultCruise;
    titleStyle?: any;
    hidePackageFrame?: boolean;
    isFavorite?: boolean;
    addToFavorites?: typeof addToFavorites;
    removeFromFavorites?: typeof removeFromFavorites;
    hideFavoritesIcon?: boolean;
    portalId?: string;
    deviceType?: string;
    sortString?: string;
    isVisible?: boolean;
    index?: number | 'dlp-recommendation';
    discountThreshold: number;
    searchPageType?: 'map' | 'destination' | 'ship' | 'mapWithNoUGC';
}

interface StateProps {
    pageData: any;
    search: ISearchReducer;
    history: any;
    homePage: any;
    phoneNumber: string;
    countryCode: string;
}

const pageStyle: React.CSSProperties = {
    fontSize: 20,
};

interface DistpachProps {
    loadMore: (page?: number) => Promise<boolean>;
    getRatesBulk: () => void;
    doNewSearchViaUrl: any;
    setFilterParam: any;
    clearFilterParams: (fetchFacets: boolean) => () => void;
    toggleSearchFilter: any;
    setSortOrder: (string) => any;
    setLoadingState: (isLoading: boolean) => void;
}

type Props = StateProps & DistpachProps;

const getSearchPageTitle = ({ filterParamsListActive, searchFacets }: any) => {
    const zoneId = filterParamsListActive?.find((item) => item?.paramName === 'zone')?.paramValue;
    const zoneString = searchFacets?.zoneNids?.find((item) => item?.id === parseInt(zoneId, 10))
        ?.title;

    const companyId = filterParamsListActive?.find((item) => item?.paramName === 'company')
        ?.paramValue;
    const companyString = searchFacets?.companyNids?.find(
        (item: any) => item?.id === parseInt(companyId, 10),
    )?.title;

    const shipId = filterParamsListActive?.find((item) => item?.paramName === 'ship')?.paramValue;
    const shipString = searchFacets?.shipNids?.find((item) => item?.id === parseInt(shipId, 10))
        ?.title;

    let title = zoneString
        ? __('%s Cruises', 'dreamlines', zoneString)
        : __('All Cruises', 'dreamlines');

    if (companyString) {
        title += ' ' + __('with %s (company)', 'dreamlines', companyString);
    }

    if (shipString) {
        title += ' ' + __('Abboard the %s (ship)', 'dreamlines', shipString);
    }

    return title;
};

class SearchPageClass extends React.Component<Props, any> {
    hasMore: boolean;
    searchFilterRef: React.RefObject<any>;

    constructor(props) {
        super(props);

        this.hasMore = true;
        this.searchFilterRef = React.createRef();

        const deviceType = this.props?.pageData?.deviceType;

        this.state = {
            isDesktop: deviceType === 'desktop',
            firstLoad: true,
            experimentVariant: '',
            shouldShowSearchResults: false,
        };

        this.props?.getRatesBulk();
    }

    componentDidMount() {
        this.resize();

        const shouldShowSearchResults =
            (this.props.search.searchResults.cruises &&
                this.props.search.searchResults.cruises.length > 0) ||
            this.props.search.searchResults.priorityCruise;

        this.setState({ shouldShowSearchResults });

        window.addEventListener('resize', this.resize);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.loadMore);
        window.removeEventListener('resize', this.resize);
    }

    resize = () => {
        if (IsomorphicHelpers.isServerSide) return;

        const width = window.innerWidth;

        const isDesktop = width >= 1200;
        if (this.state.isDesktop !== isDesktop || this.state.firstLoad) {
            this.setState({ isDesktop, firstLoad: false });
            if (isDesktop) {
                window.removeEventListener('scroll', this.loadMore);
            } else {
                window.addEventListener('scroll', this.loadMore);
            }
        }
    };

    loadMore = () => {
        const scrollY =
            window.pageYOffset ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0;
        const loadTriggerThreshold = document.body.scrollHeight - 900;
        if (
            loadTriggerThreshold < scrollY + window.innerHeight &&
            !this.props.search.isLoading &&
            !this.props.search.isSearchFilterOpen
        ) {
            this.props.loadMore().then((dataLoaded: boolean) => {
                if (dataLoaded) this.props?.getRatesBulk();
            });
        }
    };

    setSortOrder = (sortOrder: string) => {
        this.props.setSortOrder(sortOrder);
        this.props.doNewSearchViaUrl();
    };

    render() {
        const { search, pageData } = this.props;
        const { maxDiscount, cruises, priorityCruise } = search.searchResults;

        const { defaultCurrency, dualCurrency } = pageData?.appConfig;

        const activeFilterProps = {
            search,
            removeFilter: this.searchFilterRef?.current?.removeActiveFilter,
            clearFilterParams: this.searchFilterRef?.current?.clearFilterParams(),
            currency: defaultCurrency,
            dualCurrency: dualCurrency,
            exchangeRate: pageData?.header?.exchangeRate
                ? pageData.header.exchangeRate.exchangeRate
                : null,
        };
        const noSearchResults =
            (!cruises || cruises.length === 0) && !priorityCruise && !search.isLoading;

        return (
            <div style={pageStyle} className="container searchPage">
                <div className="row headline onlyDesktop">
                    {Boolean(maxDiscount) && (
                        <Discount amount={maxDiscount} showUpToText={true} size="L" />
                    )}
                    <h1 style={{ ...(maxDiscount ? {} : { marginLeft: 0 }) }}>
                        {getSearchPageTitle(search)}
                    </h1>
                </div>
                <div className="row">
                    <div className="searchPageFilterWrapper">
                        {(search.isSearchFilterOpen || this.state.isDesktop) && (
                            <SearchFilters
                                ref={this.searchFilterRef}
                                isDesktop={this.state.isDesktop}
                            />
                        )}
                        {this.state.isDesktop && (
                            <FAQContact
                                countryCode={this.props.countryCode}
                                phoneNumber={this.props.phoneNumber}
                                contactPageUrl={pageData?.brandConfig?.contactPagePath}
                            />
                        )}
                    </div>
                    <FilterAndSortBar
                        toggleSearchFilter={this.props.toggleSearchFilter}
                        setSortOrder={this.setSortOrder}
                        sortOrder={search.searchParams.sortString}
                    />
                    <div className="searchPageResultWrapper">
                        {this.state.isDesktop && <ActiveFilters {...activeFilterProps} />}
                        {this.state.shouldShowSearchResults && (
                            <SearchResultList setSortOrder={this.setSortOrder} />
                        )}
                        {noSearchResults && (
                            <NoResult
                                isLoading={search.isLoading}
                                clearFilterParams={this.props.clearFilterParams}
                                toggleSearchFilter={this.props.toggleSearchFilter}
                                search={search}
                            />
                        )}
                        {search.isLoading && <LoadingIndicator />}
                    </div>
                </div>
            </div>
        );
    }
}

// @TODO Fix state type inconsistency
const mapStateToProps = (state: StateProps) => ({
    pageData: state.pageData,
    search: state.search,
    history: (state as any).router.history,
    homePage: state.homePage,
    phoneNumber: state.pageData.brandConfig.phoneNumber,
    countryCode: state.pageData.appConfig.countryCode,
});

const mapDispatchToProps = {
    loadMore,
    getRatesBulk,
    doNewSearchViaUrl,
    setFilterParam,
    clearFilterParams,
    toggleSearchFilter,
    setSortOrder,
    setLoadingState,
};

export const SearchPage = connect(mapStateToProps, mapDispatchToProps)(SearchPageClass);
export default SearchPage;
